import React from "react";
import { useLocation } from "react-router-dom";
import SEO from "../../data/seo";
import INFO from "../../data/user";
import SeoMeta, { makeDescription } from "./SeoMeta";

const ROUTES = [
	{ match: /^\/$/, page: "home", title: "Jabu Zwane | Executive Coach, Keynote Speaker & Facilitator" },
	{ match: /^\/admin(?:\/|$)/, title: "Admin Portal | Jabu Zwane" },
	{ match: /^\/about\/?$/, page: "about", title: "About Jabu Zwane | Leadership Coach & Speaker" },
	{ match: /^\/projects\/?$/, page: "projects", title: "Jabu Zwane in the Media | Interviews & Features" },
	{ match: /^\/media\/?$/, page: "projects", title: "Jabu Zwane in the Media | Interviews & Features" },
	{ match: /^\/articles\/?$/, page: "store", title: "Books | Jabu Zwane" },
	{ match: /^\/contact\/?$/, page: "contact", title: "Book Jabu Zwane | Speaking, Coaching & Facilitation" },
	{ match: /^\/booking\/reschedule\/?$/, title: "Reschedule Appointment | Jabu Zwane" },
	{ match: /^\/events\/?$/, page: "events", title: "Events | Jabu Zwane" },
	{ match: /^\/keynotes\/?$/, page: "keynotes", title: "Keynotes | Jabu Zwane" },
	{ match: /^\/blog\/?$/, page: "blog", title: "Blog | Jabu Zwane" },
	{ match: /^\/blog\/[^/]+\/?$/, title: "Blog | Jabu Zwane" },
	{ match: /^\/article\/[^/]+\/?$/, title: "Article | Jabu Zwane" },
];

const EXCLUDED_PATHS = [
	/^\/admin(?:\/|$)/,
	/^\/events\/success\/?$/,
	/^\/articles\/success\/?$/,
	/^\/booking\/success\/?$/,
	/^\/booking\/reschedule\/?$/,
];

function getPageData(pathname) {
	const route = ROUTES.find(({ match }) => match.test(pathname));
	const seo = route?.page && SEO.find((item) => item.page === route.page);
	return {
		title: seo?.title || route?.title || "Page Not Found | Jabu Zwane",
		description: makeDescription(
			seo?.description,
			"Executive coaching, keynote speaking, leadership development and personal growth with Jabu Zwane."
		),
		keywords: seo?.keywords,
	};
}

function personSchema() {
	return {
		"@context": "https://schema.org",
		"@type": "Person",
		name: INFO.main.name,
		url: "https://www.jabuzwane.co.za/",
		image: "https://www.jabuzwane.co.za/lg.png",
		jobTitle: "Executive Coach, Keynote Speaker and Facilitator",
		description: "Award-winning leadership coach, keynote speaker, counsellor, author and media contributor.",
		sameAs: Object.values(INFO.socials).filter((value) => /^https:\/\//.test(value)),
		knowsAbout: [
			"Leadership development",
			"Executive coaching",
			"Effective communication",
			"Emotional intelligence",
			"Entrepreneurship",
		],
	};
}

const RouteSEO = () => {
	const { pathname } = useLocation();
	const canonicalPath = pathname === "/media" ? "/projects" : pathname.replace(/\/+$/, "") || "/";
	const page = getPageData(pathname);
	const noindex = EXCLUDED_PATHS.some((pattern) => pattern.test(pathname)) ||
		!ROUTES.some(({ match }) => match.test(pathname));
	const structuredData = pathname === "/"
		? [
			personSchema(),
			{
				"@context": "https://schema.org",
				"@type": "WebSite",
				name: INFO.main.name,
				url: "https://www.jabuzwane.co.za/",
			},
		]
		: {
			"@context": "https://schema.org",
			"@type": "WebPage",
			name: page.title,
			description: page.description,
			url: `https://www.jabuzwane.co.za${canonicalPath}`,
			isPartOf: {
				"@type": "WebSite",
				name: INFO.main.name,
				url: "https://www.jabuzwane.co.za/",
			},
		};

	return (
		<SeoMeta
			title={page.title}
			description={page.description}
			path={canonicalPath}
			keywords={page.keywords}
			noindex={noindex}
			structuredData={structuredData}
		/>
	);
};

export default RouteSEO;
